import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import { companyInfo } from '../../mockData';

const DesertHotSpringsPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Desert Hot Springs CA | Legal Document Services | Legal Document Specialist"
        description="Desert Hot Springs wellness community process server. Expert service for spa resorts, therapeutic hot springs facilities, and residential healing communities. Health industry legal document expertise."
        keywords="process server Desert Hot Springs CA, legal document services Desert Hot Springs, court filing Desert Hot Springs, skip tracing Desert Hot Springs, Riverside County process server"
        canonical="https://legaldocspecialist.com/desert-hot-springs-process-server"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Desert Hot Springs, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Reliable legal document services in Desert Hot Springs, CA. Professional, registered and bonded process servers serving the northern Coachella Valley with urgent and same-day options.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas & Types */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Desert Hot Springs Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive legal support services for Desert Hot Springs residents, businesses, and legal professionals.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {/* Service Types */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Our Services in Desert Hot Springs
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                All services backed by professional registration and bonding for your protection.
              </p>
            </div>

            {/* Local Court Info */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <MapPin size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Court Coverage & Filing
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Palm Springs Courthouse</strong><br />
                  3255 E Tahquitz Canyon Way<br />
                  Palm Springs, CA 92262<br />
                  <span style={{ color: 'var(--text-secondary)' }}>Closest courthouse (5 minutes from Desert Hot Springs)</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Indio Courthouse (Larson Justice Center)</strong><br />
                  46-200 Oasis Street<br />
                  Indio, CA 92201<br />
                  <span style={{ color: 'var(--text-secondary)' }}>Primary Coachella Valley courthouse (25 minutes)</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Complete knowledge of both local Coachella Valley courthouses and all Desert Hot Springs area filing requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Advantage */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for Desert Hot Springs?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Local expertise combined with professional service standards. We understand Desert Hot Springs and the unique needs of northern Coachella Valley communities.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <MapPin size={32} />,
                title: 'Local Expertise',
                description: 'Familiar with Desert Hot Springs neighborhoods, local courts, and Riverside County filing procedures.'
              },
              {
                icon: <Clock size={32} />,
                title: 'Flexible Timing',
                description: 'Routine, priority, urgent, and on-demand service levels to meet your specific deadlines.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Professional Standards',
                description: 'Registered and bonded process servers following all California legal requirements and best practices.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Desert Hot Springs Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Desert Hot Springs and the entire northern Coachella Valley region with reliable, professional service.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Communities We Serve from Desert Hot Springs
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Desert Hot Springs', 'Cathedral City', 'Palm Springs', 'North Palm Springs',
                'Thousand Palms', 'Sky Valley', 'Cabazon', 'Whitewater',
                'Morongo Valley', 'Yucca Valley', 'Joshua Tree', 'Twentynine Palms'
              ].map((city) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ color: 'var(--text-primary)' }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Complete coverage of Riverside County and San Bernardino County communities, plus statewide California service.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Desert Hot Springs Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for fast, professional service in Desert Hot Springs.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Contact Information */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Contact Information</h3>
              
              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Phone size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.phone}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Monday-Friday 9AM-5PM PST</div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Mail size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>24/7 email support</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MapPin size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Legal Document Specialist</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{companyInfo.address}</div>
                </div>
              </div>
            </div>

            {/* Service Request */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              textAlign: 'center'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Ready to Get Started?</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                Choose your service option for Desert Hot Springs:
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <Link to="/place-order" className="btn-primary" style={{ 
                  textDecoration: 'none',
                  display: 'block',
                  marginBottom: '1rem'
                }}>
                  Place Order Online
                </Link>
                <Link to="/quote" className="btn-secondary" style={{ 
                  textDecoration: 'none',
                  display: 'block'
                }}>
                  Request Free Quote
                </Link>
              </div>

              <div style={{ 
                backgroundColor: 'var(--bg-page)', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid var(--border-light)'
              }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                  <strong>Service Levels Available:</strong><br />
                  Routine (4 days) • Priority (Next Day) • Urgent (Same Day) • On-Demand (Under 2 hours)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesertHotSpringsPage;